import { UIIcon } from "@dropbox/dig-icons";
import { AddCircleLine, CloseLine } from "@dropbox/dig-icons/dist/mjs/assets";

import { IconButton } from "@dropbox/dig-components/buttons";
import { Title } from "@dropbox/dig-components/typography";
import { PAP_Click_ChatChip } from "@mirage/analytics/events/types/click_chat_chip";
import { PAP_Click_NewSessionButton } from "@mirage/analytics/events/types/click_new_session_button";
import { useAIToolLibrary } from "@mirage/mosaics/AIToolLibrary/hooks/useAIToolLibrary";
import { useAIToolLibraryModal } from "@mirage/mosaics/AIToolLibrary/hooks/useAIToolLibraryModal";
import type { ClickableEmptyChatInstructionsOption } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { EmptyChatInstructions } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { FSSEmptyChatInstructions } from "@mirage/mosaics/Chat/components/chat/instructions/FSSEmptyChatInstructions";
import { getUserMessageOptionsFromWorkflowAgent } from "@mirage/mosaics/Chat/components/chat/instructions/initializeWorkflowAgentConversation";
import { ChatGrid } from "@mirage/mosaics/Chat/components/layouts/ChatPageLayout";
import type {
  ChatSourceFssFile,
  ChatSourceFssFolder,
} from "@mirage/mosaics/Chat/types";
import { SidePanelContainer } from "@mirage/mosaics/ComposeAssistant/components/side-panel/SidePanelContainer";
import { useComposeAnalyticsContext } from "@mirage/mosaics/ComposeAssistant/data/ComposeAnalyticsContext";
import { useComposeCurrentSessionContext } from "@mirage/mosaics/ComposeAssistant/data/current-session/ComposeCurrentSessionContext";
import { useOnboardingMode } from "@mirage/mosaics/ComposeAssistant/onboarding/useOnboardingMode";
import { useAddSources } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useAddSources";
import { useRouteHandling } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useRouteHandling";
import { useWorkflowAgentHandling } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useWorkflowAgentHandling";
import { useWriteHandling } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent/useWriteHandling";
import { useGetConversationAttachments } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useConversationAttachments";
import { useManuallyAddedSources } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import { useConversationPageLoadingStates } from "@mirage/mosaics/ConversationAssistant/hooks/useConversationPageLoading";
import { useCloseOmniboxSearchSession } from "@mirage/mosaics/Omnibox/hooks/useCloseOmniboxSearchSession";
import {
  appendManualMessages,
  postUserMessage,
} from "@mirage/service-conversation";
import { useConversationRouterState } from "@mirage/service-conversation/hooks/useConversationRouterState";
import type { ConversationRouterState } from "@mirage/service-conversation/router_state_types";
import { ConversationType } from "@mirage/service-conversation/router_state_types";
import type {
  DocumentInfo,
  ModelId,
  UserMessageOptions,
  WorkflowAgent,
} from "@mirage/service-conversation/types";
import { getDashActionSurfaceComponentLoggingSource } from "@mirage/service-conversation/utils/loggingUtils";
import {
  DashSearch,
  NoSearch,
  SearchParams,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { useActionEvents } from "@mirage/service-fss-bridge/hooks/bridgeObservers";
import { tagged } from "@mirage/service-logging";
import { DashSpinnerAutofitContainer } from "@mirage/shared/dash-spinner/DashSpinnerAutofitContainer";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import { useIsSmallSizeForSidebar } from "@mirage/shared/responsive/mobile";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import { ScenarioBackButton } from "../components/ScenarioBackButton";
import { StackChatPanel } from "../components/stack-chat/StackChatPanel";
import { useConversationProgress } from "../hooks/progress/useConversationProgress";
import type { SelectedTemplate } from "../hooks/useConversationDraftConfig";
import { useFilteredMessages } from "../hooks/useFilteredMessages";
import { mapOptionToChatChip } from "../utils/chatChipMapping";
import styles from "./ConversationAssistantPageContent.module.css";
import { ConversationChat } from "./ConversationChat";
import { ConversationList } from "./ConversationList";

const logger = tagged("ConversationAssistantPageContent");

export interface ConversationAssistantPageContentProps {
  variant?: "default" | "condensed" | "workflow-agent-preview";
  shouldUseRouterNavigate?: boolean;
  hideTitle?: boolean;
  leftTitleActions?: React.ReactNode;
  rightTitleActions?: React.ReactNode;
  emptyState?: React.ReactNode;
  getTransientSource?: () => Promise<DocumentInfo>;
  onCloseWorkflowAgentPreview?: () => void;
  previewWorkflowAgent?: WorkflowAgent;
  onConversationIdChange?: (conversationId?: string) => void;
  onRequestNewSession?: (
    handler: (routerState?: ConversationRouterState) => void,
  ) => void;
  showModelPickerButton?: boolean;
  showIncognitoButton?: boolean;
}

export const ConversationAssistantPageContent = memo(
  ({
    variant = "default",
    shouldUseRouterNavigate,
    hideTitle,
    leftTitleActions,
    rightTitleActions,
    emptyState,
    getTransientSource,
    onCloseWorkflowAgentPreview,
    previewWorkflowAgent,
    onConversationIdChange,
    onRequestNewSession,
    showModelPickerButton = true,
    showIncognitoButton = true,
  }: ConversationAssistantPageContentProps) => {
    const {
      conversationId,
      conversation,
      conversationType,
      isConversationLoading,
      stackChatDetails,
      workflowAgentConfig,
      scenarioMeta,
      navigateNewConversation,
      navigateNewWorkflowAgent,
    } = useRouteHandling(
      variant,
      onConversationIdChange,
      previewWorkflowAgent,
      shouldUseRouterNavigate,
    );
    const isSmallSize = useIsSmallSizeForSidebar();
    const { currentSessionID } = useComposeCurrentSessionContext();
    const { logComposeEvent } = useComposeAnalyticsContext({
      actionSurfaceComponent: "compose_chat_pane",
      currentSessionID,
    });
    const [selectedModel, setSelectedModel] = useState<ModelId>("");
    const { manuallyAddedSources, setManuallyAddedSources, addSource } =
      useManuallyAddedSources();
    const [userMessageOptions, setUserMessageOptions] =
      useState<UserMessageOptions>({
        searchParams: new SearchParams({
          searchTarget: {
            value: new DashSearch(),
            case: "dashSearch",
          },
        }),
      });
    const getConversationAttachments = useGetConversationAttachments(
      stackChatDetails,
      workflowAgentConfig.workflowAgent,
      manuallyAddedSources,
      getTransientSource,
      conversation?.messages || [],
      userMessageOptions?.browserContext?.selectedTabIDs,
    );
    const { messagesToDisplay, templateSelectedMessage } = useFilteredMessages(
      conversation,
      workflowAgentConfig.workflowAgent?.id,
    );
    const { isLibraryOpen, activeTab, openLibrary, closeLibrary, switchTab } =
      useAIToolLibraryModal();
    const {
      progressComponent,
      isWaitingForResponse,
      shouldHideSources,
      showFollowUpSuggestionsSkeleton,
    } = useConversationProgress(conversationId, userMessageOptions);
    const writeHandling = useWriteHandling(
      conversationId,
      conversation,
      isWaitingForResponse,
      selectedModel,
      userMessageOptions,
      setUserMessageOptions,
      setManuallyAddedSources,
      logComposeEvent,
      getConversationAttachments,
      templateSelectedMessage,
      navigateNewConversation,
      openLibrary,
    );
    const { isConversationPageLoading, setIsConversationPageLoading } =
      useConversationPageLoadingStates(conversation, conversationId);
    const {
      isAddSourceModalOpen,
      setIsAddSourceModalOpen,
      handleClickAddSources,
      handleCloseAddSourcesModal,
    } = useAddSources(
      logComposeEvent,
      writeHandling.getAutoMessagesOnFirstSource,
      manuallyAddedSources,
      setManuallyAddedSources,
      setUserMessageOptions,
    );
    useWorkflowAgentHandling(
      variant,
      workflowAgentConfig,
      conversationId,
      setUserMessageOptions,
      setSelectedModel,
    );
    useConversationRouterState({
      agentPayload: writeHandling.draftConfig.agentPayload,
      chatSessionId: currentSessionID,
      conversationId,
      setSelectedVoiceID: writeHandling.draftConfig.setSelectedVoiceID,
      setSelectedTemplate: (template: SelectedTemplate) => {
        // When triggering a new chat session with a pre-selected template, drop the user into
        // empty chat flow to prompt for sources.
        setEmptyChatOption("write");
        writeHandling.draftConfig.onTemplateChange(template.id, template.name);
      },
      setUserMessageOptions,
      setManuallyAddedSources,
    });

    const { closeOmniboxSearchSessionIfExists } =
      useCloseOmniboxSearchSession();

    // For home page/empty chat instruction flickering issues setting isSubmittingMessage
    // to true will resolve the issue
    const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
    useEffect(() => {
      if (!isWaitingForResponse) {
        setIsSubmittingMessage(false);
      }
    }, [isWaitingForResponse, setIsSubmittingMessage]);

    // Adapter function to bridge the old-style postUserMessage to the new service-conversation version for onboarding mode
    const postUserMessageAdapter = useCallback(
      async (params: { text?: string; rawPromptText?: string }) => {
        if (conversationId) {
          setIsSubmittingMessage(true);
          const attachments = await getConversationAttachments();
          postUserMessage({
            message: params.text || "",
            conversationId,
            attachments,
            source:
              getDashActionSurfaceComponentLoggingSource("compose_chat_pane"),
            searchParams: new SearchParams({
              searchTarget: {
                value: new NoSearch(),
                case: "noSearch",
              },
            }),
          });
        }
      },
      [conversationId, getConversationAttachments],
    );

    const { onboardingMode } = useOnboardingMode({
      addSource,
      logComposeEvent,
      postUserMessage: postUserMessageAdapter,
      setIsAddSourceModalOpen,
      sources: manuallyAddedSources,
    });
    useEffect(() => {
      if (
        onboardingMode &&
        userMessageOptions.searchParams?.searchTarget.case !== "noSearch"
      ) {
        // This makes sure that the suggestion chips do not trigger a search
        // This is needed because most users won't have contents that can answer the question when
        // they are first onboarded.
        setUserMessageOptions({
          ...userMessageOptions,
          searchParams: new SearchParams({
            searchTarget: {
              value: new NoSearch(),
              case: "noSearch",
            },
          }),
        });
      }
    }, [onboardingMode, userMessageOptions, setUserMessageOptions]);

    useLayoutEffect(() => {
      closeOmniboxSearchSessionIfExists("assist", {
        conversationId: conversation?.serverConversationId,
      });
    }, [conversation?.serverConversationId, closeOmniboxSearchSessionIfExists]);

    useEffect(() => {
      // Set conversation level attachments, meant to stick with the conversation with every message
      if (stackChatDetails) {
        // If the current conversation is a Stack Chat, we want to skip search on the user's first
        // message
        if (!stackChatDetails.hasUserSentFirstMessage) {
          setUserMessageOptions({
            searchParams: new SearchParams({
              searchTarget: {
                value: new NoSearch(),
                case: "noSearch",
              },
            }),
          });
        }
      } else if (workflowAgentConfig.workflowAgent?.sources) {
        setUserMessageOptions(
          getUserMessageOptionsFromWorkflowAgent(
            workflowAgentConfig.workflowAgent,
          ),
        );
      }
    }, [
      stackChatDetails,
      workflowAgentConfig.workflowAgent,
      setUserMessageOptions,
    ]);

    const isConversationAssistantEnabled = convertFeatureValueToBool(
      useFeatureFlagValue("dash_2025_02_11_conversation_assistant"),
    );

    const [emptyChatOption, setEmptyChatOption] = useState<
      ClickableEmptyChatInstructionsOption | undefined
    >(undefined);
    const showEmptyChatInstructions =
      !isConversationPageLoading &&
      !isConversationLoading &&
      conversation !== undefined &&
      !conversation?.messages?.length &&
      !emptyChatOption &&
      manuallyAddedSources.length === 0 &&
      !isSubmittingMessage &&
      !onboardingMode;

    const { aiToolLibraryModal } = useAIToolLibrary({
      onTemplateChange: writeHandling.draftConfig.onTemplateChange,
      onVoiceChange: writeHandling.draftConfig.onVoiceChange,
      onPostMessageWithVoice: writeHandling.draftConfig.onPostMessageWithVoice,
      onPostMessageWithTemplate:
        writeHandling.draftConfig.onPostMessageWithTemplate,
      setUserMessageOptions,
      setEmptyChatOption,
      logComposeEvent,
      currentTemplateID: writeHandling.draftConfig.selectedTemplate?.id,
      currentVoiceID: writeHandling.draftConfig.selectedVoiceID,
      markdownDraft: conversation?.markdownDraft || "",
      isLibraryOpen,
      activeTab,
      openLibrary,
      closeLibrary,
      switchTab,
    });

    const isEmbed = isEmbedPage();
    const actionEvent = useActionEvents("add.source");

    useEffect(() => {
      if (actionEvent && actionEvent.data) {
        const sources: (ChatSourceFssFile | ChatSourceFssFolder)[] =
          actionEvent.data.sources.map((source) =>
            source.type === "file"
              ? {
                  type: "fss_file",
                  url: source.href ?? null,
                  fileId: source.fileId,
                  name: source.name,
                }
              : {
                  type: "fss_folder",
                  fileId: source.fileId,
                  path: source.path,
                },
          );
        setManuallyAddedSources((prev) => [...prev, ...sources]);
      }
    }, [setManuallyAddedSources, actionEvent]);

    const startingMessageNode = showEmptyChatInstructions ? (
      isEmbed ? (
        <FSSEmptyChatInstructions />
      ) : (
        (emptyState ?? (
          <EmptyChatInstructions
            chatWithDashEnabled
            onClickOption={(opt: ClickableEmptyChatInstructionsOption) => {
              logComposeEvent(
                PAP_Click_ChatChip({
                  chatChip: mapOptionToChatChip(opt),
                }),
              );
              if (opt === "library") {
                // Don't set emptyChatOption for library - just open the modal
                openLibrary();
              } else {
                setEmptyChatOption(opt);
                if (opt === "chat_with_dash") {
                  if (!conversationId) {
                    throw new Error(
                      "Attempting to set option without conversationId",
                    );
                  }
                  appendManualMessages([
                    {
                      conversationId: conversationId,
                      message: i18n.t(
                        "assistant_initial_generic_prompt_message",
                      ),
                      llmIgnore: true,
                      messageType: "assistantMessage",
                      source:
                        getDashActionSurfaceComponentLoggingSource(
                          "compose_chat_pane",
                        ),
                    },
                  ]);
                } else if (opt === "write") {
                  setUserMessageOptions({ agentIds: ["write"] });
                } else if (opt === "create-ai-tool") {
                  navigateNewWorkflowAgent();
                }
              }
            }}
          />
        ))
      )
    ) : isConversationPageLoading && !conversation?.messages?.length ? (
      // transition for router redirects is fast enough that showing a spinner just shows another flash
      // instead of preventing the flash of the empty state content. loading state for hydration of an
      // existing conversation is already handled at the page level by isConversationLoading
      <></>
    ) : (
      // non-empty chat shows initial input messages if chat was started with Write
      <>
        {emptyChatOption === "write" &&
          writeHandling.getWriteContextInputMessages(handleClickAddSources)}
      </>
    );

    // Exists separately from the below because callbacks for mouse events cannot take parameters
    const handleStartNewSessionWithState = useCallback(
      (routerState?: ConversationRouterState) => {
        logger.info("Starting a new session with state: ", routerState);
        setEmptyChatOption(undefined);
        setUserMessageOptions({
          searchParams: new SearchParams({
            searchTarget: {
              value: new DashSearch(),
              case: "dashSearch",
            },
          }),
        });

        // Reset model to auto ("") when the user was previously in web search mode
        // (noSearch + "openai:gpt-4o-search-preview" model)
        const wasInWebSearchMode =
          userMessageOptions.searchParams?.searchTarget?.case === "noSearch" &&
          selectedModel === "openai:gpt-4o-search-preview";
        if (wasInWebSearchMode) {
          setSelectedModel("");
        }

        // Stay in incognito mode if the user was in incognito mode
        const wasInIncognitoMode = userMessageOptions.skipPersistence === true;
        if (wasInIncognitoMode) {
          setUserMessageOptions({
            ...userMessageOptions,
            skipPersistence: true,
          });
        }

        writeHandling.draftConfig.resetDraftConfigState();
        setIsConversationPageLoading(true);
        navigateNewConversation(routerState);
      },
      [
        userMessageOptions,
        selectedModel,
        writeHandling.draftConfig,
        setIsConversationPageLoading,
        navigateNewConversation,
      ],
    );
    // Deduplicate the above callback, just with no parameters
    const handleStartNewSession = useCallback(() => {
      handleStartNewSessionWithState();
    }, [handleStartNewSessionWithState]);

    useEffect(() => {
      onRequestNewSession?.(handleStartNewSessionWithState);
    }, [onRequestNewSession, handleStartNewSessionWithState]);

    const sideBarChildren = useMemo(() => {
      if (writeHandling.showWritePane) return undefined;

      if (stackChatDetails) {
        return (
          <StackChatPanel
            chatSessionId={currentSessionID}
            lastUserMessage={stackChatDetails.lastUserMessage}
            onRefreshConversation={handleStartNewSessionWithState}
            serverConversationId={conversation?.serverConversationId}
            sourceDocumentIds={stackChatDetails.sourceDocumentIds}
            stackSharingId={stackChatDetails.sharingId}
          />
        );
      } else {
        return undefined;
      }
    }, [
      writeHandling.showWritePane,
      stackChatDetails,
      currentSessionID,
      handleStartNewSessionWithState,
      conversation?.serverConversationId,
    ]);

    // Controls whether attached sources are shown in the user message and whether
    // the user can add additional sources to the conversation.
    const allowCustomSources =
      // Disallow for Stack Chats
      !stackChatDetails &&
      // Disallow for workflow agents (undefined is treated as false)
      (!workflowAgentConfig.workflowAgent ||
        workflowAgentConfig.workflowAgent.allowCustomSources === true) &&
      // Disallow for start page query suggestions and omnibox suggested questions
      conversationType !== ConversationType.AskFromSuggestion;

    if (!isConversationAssistantEnabled) {
      return <Navigate to={RoutePath.CHAT} replace />;
    }

    if (!conversationId) {
      return null;
    }

    const sidePanelButtons =
      variant === "workflow-agent-preview" ? (
        <div className={styles.WorkflowAgentPreviewSidePanelButtons}>
          <IconButton
            variant="opacity"
            shape="circular"
            onClick={onCloseWorkflowAgentPreview}
          >
            <UIIcon src={CloseLine} />
          </IconButton>
          <Title size="small" weightVariant="emphasized">
            {i18n.t("assistant_ai_tool_preview_button")}
          </Title>
        </div>
      ) : (
        <>
          <ConversationList
            showListPreview={showEmptyChatInstructions}
            onOpenTemplates={() => openLibrary({ tab: "templates" })}
            handleStartNewSessionWithState={handleStartNewSessionWithState}
            handleNewSession={handleStartNewSession}
            logComposeEvent={logComposeEvent}
          />
          {!isEmbedPage() && (
            <IconButtonWithTooltip
              variant="borderless"
              tooltipProps={{
                title: i18n.t("compose_new_session"),
              }}
              onClick={() => {
                handleStartNewSession();
                logComposeEvent(
                  PAP_Click_NewSessionButton({
                    dashActionSurface: "conversation_page",
                    actionSurfaceComponent: "top_nav",
                    featureLine: "assist",
                  }),
                );
              }}
            >
              <UIIcon src={AddCircleLine} />
            </IconButtonWithTooltip>
          )}

          {scenarioMeta && scenarioMeta?.type !== "workflow_agent" && (
            <ScenarioBackButton scenario={scenarioMeta} />
          )}
        </>
      );

    const chatGrid = (
      <>
        <ChatGrid
          logChatEvent={() => {
            // TODO logging
          }}
          rightPaneExpanded={writeHandling.showWritePane}
          setExpandRightPane={writeHandling.setShowWritePane}
          toggleView={writeHandling.handleToggleMobileView}
          isEditorView={writeHandling.isEditorViewOnMobile}
          sidePane={
            <SidePanelContainer
              leftTitleActions={leftTitleActions ?? sidePanelButtons}
              handleClickAdd={handleClickAddSources}
              showWorkflowAgent={false}
              rightTitleActions={rightTitleActions}
              hideTitle={
                variant === "workflow-agent-preview" ? true : hideTitle
              }
              sidebarChildren={sideBarChildren}
            >
              <ConversationChat
                variant={
                  variant === "workflow-agent-preview" ? "default" : variant
                }
                startingMessageNode={startingMessageNode}
                conversationId={conversationId}
                handleClickAdd={handleClickAddSources}
                handleCloseAddSourcesModal={handleCloseAddSourcesModal}
                isAddSourceModalOpen={isAddSourceModalOpen}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                agent={workflowAgentConfig.workflowAgent}
                showEmptyChatInstructions={showEmptyChatInstructions}
                messages={messagesToDisplay}
                progressComponent={progressComponent}
                isWaitingForResponse={isWaitingForResponse}
                agentPayload={writeHandling.draftConfig.agentPayload}
                getConversationAttachments={getConversationAttachments}
                userMessageOptions={userMessageOptions}
                setUserMessageOptions={setUserMessageOptions}
                logComposeEvent={logComposeEvent}
                setIsSubmittingMessage={setIsSubmittingMessage}
                // Don't provide the ability to add sources if this is a Stack Chat
                allowCustomSources={allowCustomSources}
                conversationType={conversationType}
                shouldHideSources={shouldHideSources}
                showDraftGeneratedButton={
                  isSmallSize && writeHandling.showWritePane
                }
                onToggleMobileView={writeHandling.handleToggleMobileView}
                showFollowUpSuggestionsSkeleton={
                  showFollowUpSuggestionsSkeleton
                }
                showModelPickerButton={
                  showModelPickerButton && !workflowAgentConfig.workflowAgent
                }
                showContextPickerButton={!workflowAgentConfig.workflowAgent}
                showIncognitoButton={showIncognitoButton}
              />
            </SidePanelContainer>
          }
          rightPane={writeHandling.editorPane}
          showToggle={!!conversation?.markdownDraft}
        />
        {writeHandling.templateSettingsModal}
        {aiToolLibraryModal}
      </>
    );

    return isConversationLoading ? <DashSpinnerAutofitContainer /> : chatGrid;
  },
);
ConversationAssistantPageContent.displayName =
  "ConversationAssistantPageContent";
